import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { supabase } from '../../utils/supabase';
import { AuthState, User } from '../../types/auth';

interface LoginCredentials {
  email: string;
  password: string;
}

interface SignupCredentials extends LoginCredentials {
  metadata?: {
    full_name: string;
    farm_name: string;
  };
}

export const signupUser = createAsyncThunk(
  'auth/signupUser',
  async ({ email, password, metadata }: SignupCredentials, { rejectWithValue }) => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: metadata,
          emailRedirectTo: 'https://allagrotrack.netlify.app/reset-password'
        }
      });
      
      if (error) throw error;
      if (!data.user) throw new Error('Signup failed');
      
      return {
        user: data.user as User,
        session: data.session
      };
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const loginUser = createAsyncThunk(
  'auth/loginUser',
  async ({ email, password }: LoginCredentials, { rejectWithValue }) => {
    try {
      // Check if account is locked - use maybeSingle() to handle no rows
      const { data: metadata } = await supabase
        .from('users_metadata')
        .select('locked_until, failed_login_attempts')
        .eq('email', email)
        .maybeSingle();

      if (metadata?.locked_until && new Date(metadata.locked_until) > new Date()) {
        const waitMinutes = Math.ceil(
          (new Date(metadata.locked_until).getTime() - new Date().getTime()) / 60000
        );
        throw new Error(`Account is locked. Please try again in ${waitMinutes} minutes.`);
      }

      const { data, error } = await supabase.auth.signInWithPassword({ 
        email, 
        password 
      });
      
      if (error) throw error;
      if (!data.user || !data.session) throw new Error('Login failed');
      
      return {
        user: data.user as User,
        session: data.session
      };
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const logoutUser = createAsyncThunk(
  'auth/logoutUser',
  async (_, { rejectWithValue }) => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      return null;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const resetPassword = createAsyncThunk(
  'auth/resetPassword',
  async (email: string, { rejectWithValue }) => {
    try {
      // Determine if we're in a mobile app environment
      const isMobileApp = window.location.protocol === 'capacitor:' || 
                         window.location.protocol === 'ionic:' ||
                         /android|iphone|ipad|ipod/i.test(navigator.userAgent);
      
      // Set the appropriate redirect URL based on environment
      let redirectTo;
      
      if (isMobileApp) {
        // For mobile apps, use the custom URL scheme
        redirectTo = 'agrotrack://reset-password';
      } else {
        // For web, use the Netlify domain
        redirectTo = 'https://allagrotrack.netlify.app/reset-password';
      }
      
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo,
      });
      
      if (error) throw error;
      return true;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

const initialState: AuthState = {
  user: null,
  error: null,
  loading: false,
  session: null
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setUser: (state, action) => {
      state.user = action.payload;
      state.error = null;
    },
    setSession: (state, action) => {
      state.session = action.payload;
    },
    clearUser: (state) => {
      state.user = null;
      state.session = null;
      state.error = null;
    },
    clearError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Login cases
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.session = action.payload.session;
        state.error = null;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Signup cases
      .addCase(signupUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(signupUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.session = action.payload.session;
        state.error = null;
      })
      .addCase(signupUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Logout cases
      .addCase(logoutUser.pending, (state) => {
        state.loading = true;
      })
      .addCase(logoutUser.fulfilled, (state) => {
        state.user = null;
        state.session = null;
        state.error = null;
        state.loading = false;
      })
      .addCase(logoutUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Reset password cases
      .addCase(resetPassword.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(resetPassword.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { setUser, clearUser, clearError, setSession } = authSlice.actions;
export default authSlice.reducer;